import React, { useMemo, useState } from "react";
import { Clock, Loader2, MessageSquare, MoreHorizontal, Pencil, Send, Trash2, User, X } from "lucide-react";
import {
  useSponsorConversationLogsQuery,
  useSponsorConversationLogCreate,
  useSponsorConversationLogUpdate,
  useSponsorConversationLogDelete,
} from "@/lib/api/requests/sponsors.requests";
import type { SponsorConversationLogResponseDto } from "@/lib/api/interfaces/sponsor-conversation-logs.interface";
import { UserInfoUserRole } from "@/lib/api/interfaces/user-profile.interface";
import { Button } from "@/ui-components/ui/button";
import { useUser } from "@/lib/hooks/use-user";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { Dialog, DialogHeader, DialogOverlay, DialogPortal, DialogClose } from "@/ui-components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { SectionHeader } from "@/util-components/page-layout";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/util-components/dropdown-menu";

interface SponsorConversationLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sponsorId: string;
}

export const SponsorConversationLogDialog: React.FC<SponsorConversationLogDialogProps> = ({
  open,
  onOpenChange,
  sponsorId,
}) => {
  const [content, setContent] = useState("");
  const [editingLogId, setEditingLogId] = useState<string | null>(null);
  const [deletingLogId, setDeletingLogId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  const { user } = useUser();

  const logsQuery = useSponsorConversationLogsQuery(sponsorId, {
    enabled: !!sponsorId && open,
  });

  const createMutation = useSponsorConversationLogCreate(sponsorId, {
    onSuccess: () => {
      setContent("");
    },
  });

  const updateMutation = useSponsorConversationLogUpdate(sponsorId, {
    onSuccess: () => {
      setEditingLogId(null);
      setEditContent("");
    },
  });

  const deleteMutation = useSponsorConversationLogDelete(sponsorId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;
    createMutation.mutate({ content: trimmed });
  };

  const logs: SponsorConversationLogResponseDto[] = useMemo(
    () => (Array.isArray(logsQuery.data) ? logsQuery.data : []),
    [logsQuery.data]
  );

  const currentUserName = user?.firstName + " " + user?.lastName;

  const isLogEditable = (log: SponsorConversationLogResponseDto) => {
    if (editingLogId) {
      return editingLogId === log._id;
    }
    // Admin/Chair darf alles bearbeiten
    if (user?.userRole === UserInfoUserRole.ADMIN) return true;
    
    // Ansonsten darf jeder seine eigenen Einträge bearbeiten
    return log.authorName === currentUserName;
  };

  const handleEditStart = (log: SponsorConversationLogResponseDto) => {
    if (!isLogEditable(log)) return;
    setEditingLogId(log._id);
    setEditContent(log.content ?? "");
  };

  const handleEditCancel = () => {
    setEditingLogId(null);
    setEditContent("");
  };

  const handleEditSave = () => {
    if (!editingLogId) return;
    const trimmed = editContent.trim();
    if (!trimmed) return;
    updateMutation.mutate({ logId: editingLogId, data: { content: trimmed } });
  };

  const handleDelete = (logId: string) => {
    setDeletingLogId(logId);
  };

  const confirmDelete = async () => {
    if (!deletingLogId) return;
    try {
      await deleteMutation.mutateAsync({ logId: deletingLogId });
      setDeletingLogId(null);
    } catch (error) {
      console.error("Failed to delete log entry", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/10 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed z-50 bottom-4 right-4 top-auto left-auto translate-x-0 translate-y-0 w-full max-w-[500px] h-[600px] flex flex-col p-0 overflow-hidden rounded-2xl shadow-[0_30px_90px_rgba(0,0,0,0.3)] border border-gray-200 bg-white data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-right-2"
        >
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-white">
          <div className="flex items-center justify-between gap-3">
            <SectionHeader
              icon={MessageSquare}
              title="Gesprächsprotokoll"
              description="Vollständige Historie der Interaktionen."
            />
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 bg-gray-50/30">
          {/* Scrollable Logs Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-gray-200">
            {logsQuery.isLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-10">
                <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Noch keine Einträge vorhanden.</p>
              </div>
            ) : (
              logs.map((log, idx) => {
                const dateSource = log.conversationAt || log.createdAt;
                const date = dateSource ? new Date(dateSource) : null;
                const isEditable = isLogEditable(log);
                const isEditingThis = editingLogId === log._id;

                return (
                  <div
                    key={log._id ?? `${dateSource ?? "no-date"}-${idx}`}
                    className="min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-white p-4 transition-colors hover:border-gray-200"
                  >
                    <div className="relative">
                      {isEditable && !isEditingThis && (
                        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                          <div className="absolute right-0 top-0">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-lg p-0 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  disabled={deleteMutation.isPending}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditStart(log);
                                  }}
                                  className="cursor-pointer"
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Bearbeiten
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(log._id);
                                  }}
                                  className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Löschen
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </PermissionFeatureWrapper>
                      )}

                      <div className="min-w-0 pr-9">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-50">
                            <User className="h-3.5 w-3.5 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900">
                              {log.authorName || "Unbekannter Benutzer"}
                            </p>
                            <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-400">
                              <Clock className="h-3 w-3 shrink-0" />
                              <span className="truncate">
                                {date
                                  ? date.toLocaleString("de-DE", {
                                      dateStyle: "short",
                                      timeStyle: "short",
                                    })
                                  : "Ohne Datum"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {isEditingThis ? (
                          <div className="space-y-3 pl-10 pt-3">
                            <textarea
                              className="min-h-[96px] w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 outline-none transition-colors focus:border-blue-500 focus:bg-white"
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                            />
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleEditCancel}
                                className="h-8 rounded-lg px-3 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                              >
                                Abbrechen
                              </Button>
                              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                                <Button
                                  size="sm"
                                  onClick={handleEditSave}
                                  className="h-8 rounded-lg bg-blue-600 px-4 text-xs text-white hover:bg-blue-700"
                                >
                                  Speichern
                                </Button>
                              </PermissionFeatureWrapper>
                            </div>
                          </div>
                        ) : (
                          <div className="pl-10 pt-3">
                            <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600">
                              {log.content}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Fixed Input Area at Bottom */}
          <div className="p-4 bg-white border-t border-gray-100">
            <form onSubmit={handleSubmit} className="relative">
              <textarea
                className="block w-full rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3 text-sm shadow-sm transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 min-h-[80px] max-h-[150px] resize-none pr-12"
                placeholder="Neue Notiz hinzufügen..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={createMutation.isPending}
              />
              <div className="absolute bottom-2 right-2">
                <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                  <Button
                    type="submit"
                    size="icon"
                    disabled={createMutation.isPending || !content.trim()}
                    className="rounded-lg h-9 w-9 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all"
                  >
                    {createMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </PermissionFeatureWrapper>
              </div>
            </form>
          </div>
        </div>
      </DialogPrimitive.Content>
      </DialogPortal>

      <StandardDeleteDialog
        open={!!deletingLogId}
        onOpenChange={(open) => !open && setDeletingLogId(null)}
        title="Eintrag löschen"
        description="Möchten Sie diesen Eintrag wirklich unwiderruflich löschen?"
        itemTitle={(() => {
          const log = logs.find(l => l._id === deletingLogId);
          if (!log) return "";
          return log.content.length > 50 ? log.content.substring(0, 50) + "..." : log.content;
        })()}
        onDelete={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </Dialog>
  );
};
